import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ExternalLink, Euro, Calendar, FileText, AlertTriangle, Plus, Trash2, FlaskConical, MapPin } from 'lucide-react';
import { useSubsidieRegelingen, type SubsidieRegeling } from '@/hooks/useSubsidieRegelingen';
import { useSubsidieSimulaties, type IntakeTarget } from '@/hooks/useSubsidieIntake';
import { SubsidieIntakeForm } from '@/components/subsidie/SubsidieIntakeForm';
import { SubsidieDashboard } from '@/components/subsidie/SubsidieDashboard';
import { useProjects } from '@/hooks/useProjects';
import { useChargingLocations } from '@/hooks/useChargingLocations';
import { SubsidieBeheerPaneel } from '@/components/subsidie/beheer/SubsidieBeheerPaneel';
import { BronControleBadge } from '@/components/subsidie/beheer/BronControleBadge';
import { bronVersheid } from '@/components/subsidie/beheer/bron-versheid';
import { leesBronGecontroleerdOp } from '@/hooks/useSubsidieBeheer';
import { SignalenPaneel } from '@/components/subsidie/signalen/SignalenPaneel';
import { useSubsidieSignalen } from '@/hooks/useSubsidieSignalen';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Het jaar waar dit scherm over gaat. Stond eerder als losse 2026 in de
 * hook-aanroep; nu één plek, omdat de beheerkant hem ook nodig heeft (een
 * nieuwe regeling krijgt dit jaartal mee, en `code + jaar` is uniek).
 */
const JAAR = 2026;

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-green-100 text-green-800',
  gesloten: 'bg-red-100 text-red-800',
  uitgeput: 'bg-red-100 text-red-800',
  gepland: 'bg-blue-100 text-blue-800',
  voorbehoud: 'bg-amber-100 text-amber-800',
};

export default function CRMSubsidies() {
  const [activeTab, setActiveTab] = useState('overzicht');
  const [intakeMode, setIntakeMode] = useState<'project' | 'simulatie'>('project');
  const [selectedTarget, setSelectedTarget] = useState<IntakeTarget | null>(null);
  const [newSimName, setNewSimName] = useState('');
  const { regelingen, regels, bedragen, combinaties, documenten, isLoading } = useSubsidieRegelingen(JAAR);
  const { projects } = useProjects();
  const { locations: kavels } = useChargingLocations();
  const { simulaties, createSimulatie, deleteSimulatie } = useSubsidieSimulaties();
  /**
   * Alleen voor het telletje op het tabblad. `SignalenPaneel` roept dezelfde
   * hook aan; React Query deelt de cache-ingang op `signalenQuerySleutel(jaar)`,
   * dus dit kost geen tweede query.
   */
  const { concepten: openSignalen } = useSubsidieSignalen(JAAR);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Subsidies</h1>
        <p className="text-muted-foreground">Regelingen {JAAR}, intake en subsidie-matching per project</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overzicht">Overzicht regelingen</TabsTrigger>
          <TabsTrigger value="intake">Intake & matching</TabsTrigger>
          <TabsTrigger value="combinaties">Stapelbaarheid</TabsTrigger>
          {/* Bewaking staat vóór Beheer: de vervolgstap van vrijwel elk signaal
              is een wijziging in Beheer, en het scherm stuurt daar ook heen. */}
          <TabsTrigger value="signalen" className="gap-1">
            Bewaking
            {openSignalen.length > 0 && (
              <Badge variant="secondary" className="ml-1">{openSignalen.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="beheer">Beheer</TabsTrigger>
        </TabsList>

        {/* Tab 1: Overzicht regelingen */}
        <TabsContent value="overzicht" className="space-y-4">
          {isLoading ? (
            <p className="text-muted-foreground">Laden...</p>
          ) : (
            <div className="grid gap-4">
              {regelingen.map(r => (
                <RegelingCard key={r.id} regeling={r} documenten={documenten.filter(d => d.regeling_id === r.id)} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab 2: Intake & Matching */}
        <TabsContent value="intake" className="space-y-4">
          {/* Mode toggle */}
          <div className="flex gap-2">
            <Button
              variant={intakeMode === 'project' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setIntakeMode('project'); setSelectedTarget(null); }}
            >
              Project
            </Button>
            <Button
              variant={intakeMode === 'simulatie' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setIntakeMode('simulatie'); setSelectedTarget(null); }}
            >
              <FlaskConical className="h-4 w-4 mr-1" /> Vrije simulatie
            </Button>
          </div>

          {intakeMode === 'project' ? (
            <Card>
              <CardHeader><CardTitle>Selecteer project of kavel</CardTitle></CardHeader>
              <CardContent>
                {/* T1.3 — hier stonden Marktlocaties (`laadplein_locaties`). Die
                    schreven entity_type 'laadplein_locatie', wat niet in de CHECK
                    stond, dus dat pad heeft nooit iets opgeslagen. Ze komen niet
                    terug: een Marktlocatie is marktonderzoek over plekken waar
                    misschien nooit iets gebeurt, en een subsidie-intake gaat over
                    een voorgenomen investering. Dat hoort aan een Kavel. */}
                <Select
                  value={selectedTarget ? `${selectedTarget.kind}:${selectedTarget.id}` : ''}
                  onValueChange={v => {
                    const [kind, ...rest] = v.split(':');
                    const id = rest.join(':');
                    if (kind === 'project' || kind === 'charging_location') {
                      setSelectedTarget({ kind, id });
                    }
                  }}
                >
                  <SelectTrigger className="w-full max-w-md">
                    <SelectValue placeholder="Kies een project of kavel..." />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Projecten</SelectLabel>
                        {projects.map(p => (
                          <SelectItem key={p.id} value={`project:${p.id}`}>
                            {p.name} — {(p as any).organizations?.name ?? (p as any).organization?.name ?? 'Geen organisatie'}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    {kavels.length > 0 && (
                      <SelectGroup>
                        <SelectLabel className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> Kavels
                        </SelectLabel>
                        {kavels.map((kavel: any) => (
                          <SelectItem key={kavel.id} value={`charging_location:${kavel.id}`}>
                            {kavel.name}{kavel.city ? ` — ${kavel.city}` : ''}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader><CardTitle>Simulaties</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newSimName}
                    onChange={e => setNewSimName(e.target.value)}
                    placeholder="Naam simulatie (bijv. 'Scenario A - depot Eindhoven')"
                    className="max-w-md"
                  />
                  <Button
                    onClick={async () => {
                      if (!newSimName.trim()) return;
                      const sim = await createSimulatie(newSimName.trim());
                      setSelectedTarget({ kind: 'simulatie', id: sim.id });
                      setNewSimName('');
                    }}
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Nieuw
                  </Button>
                </div>
                {simulaties.length > 0 && (
                  <div className="space-y-2">
                    {simulaties.map(sim => (
                      <div
                        key={sim.id}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-muted/50 ${
                          selectedTarget?.id === sim.id ? 'bg-muted border-primary' : ''
                        }`}
                        onClick={() => setSelectedTarget({ kind: 'simulatie', id: sim.id })}
                      >
                        <div>
                          <p className="font-medium">{sim.naam || 'Naamloos'}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(sim.updated_at).toLocaleDateString('nl-NL')} — {sim.volledigheid_pct}% ingevuld
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={e => { e.stopPropagation(); deleteSimulatie(sim.id); if (selectedTarget?.id === sim.id) setSelectedTarget(null); }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {selectedTarget && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h2 className="text-lg font-semibold mb-3">Intake-formulier</h2>
                <SubsidieIntakeForm target={selectedTarget} />
              </div>
              <div>
                <h2 className="text-lg font-semibold mb-3">Subsidie-matches</h2>
                <SubsidieDashboard target={selectedTarget} />
              </div>
            </div>
          )}
        </TabsContent>

        {/* Tab 3: Stapelbaarheid */}
        <TabsContent value="combinaties" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Combinatietabel 2026</CardTitle>
            </CardHeader>
            <CardContent>
              <StapelbaarheidMatrix regelingen={regelingen} combinaties={combinaties} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 4: Bewaking (T7.2).

            De edge-functie `subsidie-bewaking` schrijft sinds migratie 063
            conceptsignalen in `sub_bron_signalen`, mét de brontekst en het
            verschil tussen wat de database en wat de bron zegt. Tot dit tabblad
            bestond, las niets in `src/` die tabel: `grep -rn sub_bron_signalen
            src/` gaf op 08-09-2026 nul treffers. Hier worden ze beoordeeld —
            en niets raakt een regeling zonder de klik hiernaast. */}
        <TabsContent value="signalen" className="space-y-4">
          <SignalenPaneel jaar={JAAR} onNaarBeheer={() => setActiveTab('beheer')} />
        </TabsContent>

        {/* Tab 5: Beheer.

            Tot 8 september 2026 was elke correctie op een bedrag, een datum of
            een voorwaarde een migratie — de hooks deden alleen SELECT. Dat
            schaalt niet: RVO wijzigt een regeling een paar keer per jaar. Hier
            is het een handeling. */}
        <TabsContent value="beheer" className="space-y-4">
          <SubsidieBeheerPaneel
            jaar={JAAR}
            regelingen={regelingen}
            regels={regels}
            bedragen={bedragen}
            isLoading={isLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RegelingCard({ regeling, documenten }: { regeling: SubsidieRegeling; documenten: any[] }) {
  const [expanded, setExpanded] = useState(false);

  // Hoe oud de vastgelegde data is. Dit stond nergens in het scherm, en daardoor
  // stond de seed van 8 april 2026 op 8 september nog steeds ongemerkt in de
  // database — 153 dagen. Een verouderde regeling krijgt daarom ook een rand.
  const gecontroleerdOp = leesBronGecontroleerdOp(regeling);
  const versheid = bronVersheid(gecontroleerdOp, new Date());

  return (
    <Card className={versheid.status === 'vers' ? '' : 'border-amber-400 border-2'}>
      <CardContent className="py-4">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-lg">{regeling.korte_naam}</h3>
              <Badge className={STATUS_COLORS[regeling.status] ?? ''}>
                {regeling.status}
              </Badge>
              <Badge variant="outline">
                {regeling.type === 'subsidie' ? 'Subsidie' : 'Fiscaal'}
              </Badge>
              <BronControleBadge datum={gecontroleerdOp} />
            </div>
            <p className="text-sm text-muted-foreground">{regeling.naam}</p>
            <p className="text-sm">{regeling.samenvatting}</p>

            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
              <span className="flex items-center gap-1">
                <Euro className="h-3.5 w-3.5" />
                Budget: {regeling.budget_totaal ? formatCurrency(regeling.budget_totaal) : '-'}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {formatDate(regeling.aanvraag_open)} — {formatDate(regeling.aanvraag_sluit)}
              </span>
              {regeling.subsidie_percentage_max && (
                <span>Max {Math.round(regeling.subsidie_percentage_max * 100)}%</span>
              )}
            </div>

            {regeling.opmerkingen && (
              <div className="flex items-start gap-1 mt-2 text-sm text-amber-700 bg-amber-50 rounded p-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{regeling.opmerkingen}</span>
              </div>
            )}

            {expanded && documenten.length > 0 && (
              <div className="mt-3 space-y-1">
                <p className="text-sm font-medium flex items-center gap-1">
                  <FileText className="h-3.5 w-3.5" /> Documenten bij aanvraag:
                </p>
                {documenten.map(d => (
                  <div key={d.id} className="flex items-start gap-2 text-sm pl-5">
                    {/* De voorwaarde hoort naast het woord "verplicht" te staan.
                        Migratie 069 zet drie eisen die op `verplicht = false`
                        stonden terecht om — RVO eist ze zodra hun voorwaarde
                        geldt — maar zonder die voorwaarde erbij leest een
                        voorwaardelijke bijlage als een onvoorwaardelijke, en dan
                        is dit scherm strenger dan RVO zelf. */}
                    <span className="whitespace-nowrap">
                      {d.soort === 'voorwaardelijk'
                        ? '(verplicht, mits)'
                        : d.soort === 'aanbevolen' || d.verplicht === false
                          ? '(aanbevolen)'
                          : '(verplicht)'}
                    </span>
                    <span>
                      <span>{d.document_naam}</span>
                      {d.voorwaarde && (
                        <span className="text-muted-foreground"> — {d.voorwaarde}</span>
                      )}
                      {d.beschrijving && (
                        <span className="text-muted-foreground"> — {d.beschrijving}</span>
                      )}
                      {/* Een eis waarvan de herkomst niet is na te lezen wordt als
                          zodanig getoond in plaats van als vaststaand feit. */}
                      {d.soort && !d.bron_url && (
                        <span className="text-amber-600 dark:text-amber-500">
                          {' '}(herkomst niet vastgelegd)
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
              {expanded ? 'Minder' : 'Meer'}
            </Button>
            {regeling.rvo_url && (
              <a href={regeling.rvo_url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm">
                  <ExternalLink className="h-4 w-4 mr-1" /> RVO
                </Button>
              </a>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StapelbaarheidMatrix({ regelingen, combinaties }: { regelingen: SubsidieRegeling[]; combinaties: any[] }) {
  const codes = regelingen.map(r => r.code);

  const getCombinatie = (a: string, b: string) => {
    return combinaties.find(c =>
      (c.regeling_a === a && c.regeling_b === b) ||
      (c.regeling_a === b && c.regeling_b === a)
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="p-2 text-left"></th>
            {codes.map(code => (
              <th key={code} className="p-2 text-center font-medium">{code}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {codes.map(rowCode => (
            <tr key={rowCode} className="border-t">
              <td className="p-2 font-medium">{rowCode}</td>
              {codes.map(colCode => {
                if (rowCode === colCode) {
                  return <td key={colCode} className="p-2 text-center bg-muted">—</td>;
                }
                const combo = getCombinatie(rowCode, colCode);
                if (!combo) return <td key={colCode} className="p-2 text-center text-muted-foreground">n.v.t.</td>;
                return (
                  <td key={colCode} className="p-2 text-center" title={combo.toelichting ?? ''}>
                    {combo.combineerbaar ? (
                      <span className="text-green-600 font-bold" title={combo.toelichting}>&#10003;</span>
                    ) : (
                      <span className="text-red-600 font-bold" title={combo.toelichting}>&#10007;</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
